/**
 * PaymentProcessing.js
 *
 * Payment flow logic and API integration.
 * Handles QR code, BLIK, and fallback payment methods.
 */

export class PaymentProcessing {
  constructor(modal) {
    this.modal = modal
  }

  /**
   * Start QR code payment flow
   */
  async startQrPaymentFlow() {
    if (!this.modal.paymentService || !this.modal.currentOrder) {
      console.warn('[PaymentProcessing] PaymentService or order not available')
      return
    }

    try {
      const amount = this.modal.state.getOrderTotalGross(
        this.modal.currentOrder,
        this.modal.displayItems,
        this.modal.beforeOrder
      )
      this.modal.currentPayment = await this.modal.paymentService.handleTabletPayment(
        this.modal.currentOrder,
        'qr_code',
        amount
      )

      const container = document.getElementById('qr-code-container')
      if (container) {
        if (this.modal.currentPayment?.paymentUrl) {
          const encoded = encodeURIComponent(this.modal.currentPayment.paymentUrl)
          const img = document.createElement('img')
          img.alt = 'QR Code'
          img.width = 260
          img.height = 260
          img.src = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encoded}`
          container.innerHTML = ''
          container.appendChild(img)
        } else {
          container.innerHTML = '<div class="qr-code-mock" style="display:flex; align-items:center; justify-content:center; width:260px; height:260px; background:#f3f4f6; border-radius:12px; color:#9ca3af;"><i class="fas fa-qrcode fa-4x"></i></div>'
        }
      }

      if (!this.modal.paymentUnsubscribe) {
        this.modal.paymentUnsubscribe = this.modal.paymentService.subscribe((status, data) => {
          if (!data || !data.id) return
          if (status === 'completed') {
            this.handlePaymentSuccess({
              transactionId: data.id,
              method: 'qr_code',
              amount: data.amount,
              currency: this.modal.currentOrder?.currency || 'PLN'
            })
          }
          if (status === 'failed' || status === 'cancelled' || status === 'timeout') {
            this.modal.hide()
          }
        })
      }
    } catch (err) {
      console.error('[PaymentProcessing] Failed to start QR payment:', err)
    }
  }

  /**
   * Process BLIK payment
   */
  async processBLIKPayment() {
    console.log('[PaymentProcessing] Processing BLIK payment')

    const submitBtn = document.getElementById('submit-blik-code')
    if (submitBtn) {
      submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Processing...'
      submitBtn.disabled = true
    }

    try {
      if (!this.modal.paymentService || !this.modal.currentOrder) {
        throw new Error('No payment service or order')
      }

      if (!this.modal.currentPayment) {
        const amount = this.modal.state.getOrderTotalGross(
          this.modal.currentOrder,
          this.modal.displayItems,
          this.modal.beforeOrder
        )
        this.modal.currentPayment = await this.modal.paymentService.initiatePayment(
          this.modal.currentOrder,
          'blik',
          amount
        )
        this.stopListening()
        this.modal.paymentUnsubscribe = this.modal.paymentService.subscribe((status, data) => {
          if (status === 'completed') {
            this.notifyStaff('green')
            this.handlePaymentSuccess('BLIK')
          }
          if (status === 'failed' || status === 'cancelled' || status === 'timeout') {
            this.modal.hide()
          }
        })
      }

      const code = (this.modal.currentBlikCode || '').toString()
      if (!/^[0-9]{6}$/.test(code)) throw new Error('Invalid BLIK code')

      const baseUrl = `${window.__TAB__API_URL__ || ''}` || ''
      const apiBase = this.modal.paymentService?.baseUrl || `${baseUrl}/api/v1`
      const deviceId = (window.AndroidDevice && window.AndroidDevice.getDeviceId && window.AndroidDevice.getDeviceId()) || 'tablet'
      const resp = await fetch(`${apiBase}/device/payments/blik`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.modal.paymentService?.authToken || ''}`,
          'X-Device-Id': deviceId,
        },
        body: JSON.stringify({ transactionId: this.modal.currentPayment.id, code })
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error(err?.message || `HTTP ${resp.status}`)
      }
      const data = await resp.json()
      console.log('[PaymentProcessing] BLIK submit response:', data)
      if (data?.status === 'failed' || data?.status === 'cancelled') {
        throw new Error('BLIK submission rejected')
      }
    } catch (err) {
      console.error('[PaymentProcessing] BLIK submit failed:', err)
      alert('BLIK payment failed. Please try again or choose another method.')
      this.modal.hide()
    }
  }

  /**
   * Process fallback payment (staff/desk)
   */
  processFallbackPayment() {
    console.log('[PaymentProcessing] Processing fallback payment:', this.modal.currentPaymentMethod)

    if (this.modal.currentPaymentMethod === 'DESK_PAYMENT') {
      this.notifyStaff('yellow')
    }

    this.handlePaymentSuccess(this.modal.currentPaymentMethod)
  }

  /**
   * Handle successful payment
   */
  handlePaymentSuccess(payment) {
    const event = new CustomEvent('paymentCompleted', {
      detail: {
        action: 'continue',
        payment,
        order: this.modal.currentOrder,
        timestamp: Date.now()
      }
    })
    document.dispatchEvent(event)

    this.modal.hide()
  }

  /**
   * Notify staff with color indicator
   */
  notifyStaff(color) {
    console.log(`[PaymentProcessing] Notifying staff with ${color} credit card icon`)

    const event = new CustomEvent('staffNotification', {
      detail: {
        type: 'payment_status',
        color,
        message: `Payment ${color === 'green' ? 'completed' : color === 'red' ? 'assistance needed' : 'desk payment'}`,
        orderId: this.modal.currentOrder?.id,
        timestamp: Date.now()
      }
    })
    document.dispatchEvent(event)
  }

  /**
   * Stop listening to payment events
   */
  stopListening() {
    if (this.modal.paymentUnsubscribe) {
      try { this.modal.paymentUnsubscribe() } catch {}
      this.modal.paymentUnsubscribe = null
    }
  }
}
